import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import axios from "axios";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortal() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('client_token');
      if (!token) {
        setCheckingAuth(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/client/me`, { 
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.id) {
          navigate("/portal/dashboard");
        }
      } catch {
        // Token invalid, clear it
        localStorage.removeItem('client_token');
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/client/login`,
        { email, password },
        { withCredentials: true }
      );
      // Store token in localStorage as fallback
      if (response.data.token) {
        localStorage.setItem('client_token', response.data.token);
      }
      toast.success("Bienvenido");
      navigate("/portal/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Credenciales inválidas";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#F5F6F3] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#103F38]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F3]">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <PetrocontrolLogo className="h-8" />
          </a>
          <a 
            href="/"
            className="flex items-center gap-2 text-sm text-[#66727B] hover:text-[#103F38] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio
          </a>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#103F38]/10 rounded-full mb-4">
                <LogIn className="h-8 w-8 text-[#103F38]" />
              </div>
              <h1 className="text-2xl font-bold text-[#152238] mb-2">Acceso Clientes</h1>
              <p className="text-[#66727B]">Ingresa a tu portal para dar seguimiento a tus trámites</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#152238]">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@empresa.com"
                  className="border-[#e2e4e0] focus:border-[#103F38] focus:ring-[#103F38]"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#152238]">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    className="border-[#e2e4e0] focus:border-[#103F38] focus:ring-[#103F38] pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#66727B] hover:text-[#103F38]"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#103F38] hover:bg-[#0c332d] text-white rounded-full py-6 font-medium"
                data-testid="login-submit"
              >
                {loading ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#e2e4e0] text-center">
              <p className="text-sm text-[#66727B]">
                Si aún no tienes cuenta, contacta a tu ejecutivo asignado.
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-[#66727B] mt-6">
            Tu información está protegida y es completamente confidencial
          </p>
        </div>
      </div>
    </div>
  );
}
